// ROUND-009, MECHANISM 1: REAL AERIAL PERSPECTIVE, AND ONE ATMOSPHERE FOR THE
// WHOLE WORLD.
//
// WHAT WAS WRONG
//   Every world surface ran its own copy of the same three lines:
//       float fog = clamp((dist - fogSE.x) / (fogSE.y - fogSE.x), 0.0, 1.0);
//       gl_FragColor = vec4(mix(lit, fogColor, fog), 1.0);
//   i.e. LINEAR fog with a per-mood dead band. On the clear mood that band is
//   [700, 5200]: nothing inside 700 m of the camera received a single point of
//   haze, and past it the air was a flat grey lerp of one constant colour in
//   every direction. The round-8 lighting judge measured the consequence and
//   called it the single biggest reason our frames lose to DayZ:
//     - a near rooftop and a 3 km apartment block render at the SAME value and
//       the SAME chroma, so the image has no depth cue at all;
//     - fog cannot sit IN a valley, because a distance-only model has no idea
//       where the valley is;
//     - the air is the same colour looking into the sun as away from it, so a
//       distant treeline reads as paint rather than as air.
//   The mood table then spent three rounds re-tuning constants inside that
//   model, which is why none of it moved the picture.
//
// WHAT THIS IS
//   ONE function, injected into every world fragment shader, that all of them
//   consume — terrain, vegetation, buildings, interiors, fences, roads, rails,
//   glTF props, power lines and water. That is the point: sky, ground and trees
//   have to disagree by nothing at the horizon, and they cannot if each one
//   carries its own hand-rolled ramp.
//
//   Optical depth to a fragment, two physically-motivated terms:
//
//     tau = (betaR * d)^2  +  betaM * L(d, camY, fragY)
//
//   * (betaR*d)^2 is the exponential-SQUARED (Rayleigh-ish) column: uniform with
//     altitude over a 10 km map — Rayleigh's own scale height is 8 km — and
//     quadratic in distance, so it is ~0 in the near field (0.03% at 50 m, 0.5%
//     at 200 m on the clear mood) and grades continuously all the way to the
//     horizon. This is what replaces the 700 m switch-on: haze now accumulates
//     from the camera outward, it is simply too thin to see up close, which is
//     what "near objects unhazed, distant ones losing chroma" actually means.
//   * betaM * L is the aerosol/Mie ground layer, and L is the ANALYTIC integral
//     of an exponential density profile along the view ray:
//         L = d * (e^-a - e^-b) / (b - a),  a = (camY-y0)/H, b = (fragY-y0)/H
//     This is the term that puts fog IN the valley. Looking down off a ridge the
//     ray descends into denser air and L > d; looking up at a peak at the same
//     distance the ray climbs out of the layer and L << d. A distance-only model
//     cannot produce that no matter how it is tuned, and it is exactly what the
//     DayZ valley plates show. Density below the datum is capped (ashAtmQ.x) so
//     a camera on a 700 m shelf does not paint the valley floor solid white.
//
//   Air COLOUR is per-fragment and has two halves, because the two terms above
//   scatter differently:
//   * The two columns carry DIFFERENT inscatter colours, mixed by their share of
//     the optical depth. Mie/aerosol air is the mood's authored fogColor —
//     achromatic, the colour of the horizon. Rayleigh air is BLUE, and that is
//     not a taste call: it is the one thing every DayZ valley plate does that a
//     grey lerp cannot. MEASURED on the probe rig, mountain_snow full-frame
//     saturation reads 49.0 against a 56.9 reference — we are already SHORT of
//     chroma there — and a single-colour haze made it worse on every variant
//     tried (base 47.4, KM 0.8 46.8, even with the directional tint switched off
//     entirely: 47.5). Distance was removing chroma because the only thing it
//     could mix toward was a desaturated grey. Splitting the tints lets the far
//     field GAIN blue with distance the way real air does.
//   * On top of that, a view-dependent Mie tilt (Mie is strongly forward
//     scattering): the air is warmed and brightened toward the sun and cooled
//     away from it, by a broad cos^2 term plus a tight forward lobe. Per
//     FRAGMENT, not per camera — the sun side of a panorama glows and the anti-
//     sun side goes cold within the same frame, which is what makes a distant
//     treeline read as air rather than as paint.
//
// HOW IT REACHES THE SHADERS
//   Same technique as src/core/sunshadow.js and for the same reason: the world
//   is drawn by hand-written self-lit ShaderMaterials owned by five other
//   modules. The fog term is string-patched into Effect.ShadersStore at boot,
//   anchored on the shared `clamp((<dist> - fogSE.x) / ...)` idiom, so this
//   whole mechanism lives in one file instead of five, and the per-mood plumbing
//   (fogSE, fogColor, camPos, sunDir) that already exists is reused as-is.
//
//   The air COLOUR is injected as a local `vec3 fogColor` at the top of main(),
//   which shadows the uniform of the same name for the rest of main(). Every
//   existing use — terrain's mix(), vegetation's fogColor*aerCol, water's
//   premultiplied composite, the power-line thinning — therefore picks up the
//   directional air with no call-site surgery and no change to any module's
//   setColor3('fogColor', ...) plumbing. ashAtmo() itself is declared outside
//   main(), so inside it `fogColor` is still the uniform: no recursion.
//
// AND IT SUPERSEDES sunshadow.js's HEIGHT FOG (that file is NOT edited)
//   sunshadow's ashFog() raised transmittance to exp(-(y-base)/H) at the
//   RECEIVER's own height — a height term with no path integral, bolted onto a
//   linear distance ramp. Leaving it in place would apply a second, cruder
//   height falloff on top of this one, so the patcher rewrites `ashFog(fog,
//   vPos)` back to `fog` wherever sunshadow put it. The mood's `fogH` is not
//   thrown away: it is this model's 1/H, now used in the honest place.
//
// DEBUG / SAFETY
//   window.__atmo.legacy(true) restores the exact old linear model at runtime
//   (betaR < 0 is the shader's sentinel), which is both the A/B control the
//   probe rig needs and a one-line escape hatch if this ever has to be backed
//   out on the live build.
import { Effect } from '@babylonjs/core/Materials/effect';
import { ShaderMaterial } from '@babylonjs/core/Materials/shaderMaterial';
import { Vector4 } from '@babylonjs/core/Maths/math';

// Every world fragment shader that fogs. `lite` shaders (the power-line blade)
// have no vPos/camPos/sunDir in the fragment, so they get the distance-only
// half of the model — same curve, no height term, no directional tint.
const FRAGS = {
  map10k: 'full',
  // `veg` is `full` plus one extra rewrite — see VEG_AER_RE below.
  map10kVeg: 'veg',
  map10kBldg: 'full',
  map10kInterior: 'full',
  map10kFence: 'full',
  map10kRoad: 'full',
  map10kRail: 'full',
  map10kPropGlb: 'full',
  map10kWater: 'full',
  map10kWire: 'lite',
};

// ashAtmP: x = betaR (1/m, exp-squared column)   y = betaM (1/m, ground layer)
//          z = 1/H  (height falloff, 1/m)        w = layer datum height (m)
// ashAtmA: rgb = into-sun air multiplier         w = forward-lobe gain
// ashAtmB: rgb = away-from-sun air multiplier    w = forward-lobe exponent
// ashAtmQ: x = ln(max density below the datum)   yzw = Rayleigh inscatter tint
const GLSL_LITE = `
uniform vec4 ashAtmP;
uniform vec4 ashAtmQ;
float ashFogAmtD(float d) {
  if (ashAtmP.x < 0.0) return clamp((d - fogSE.x) / max(fogSE.y - fogSE.x, 1.0), 0.0, 1.0);
  float r = ashAtmP.x * d;
  return 1.0 - exp(-(r * r + ashAtmP.y * d));
}
`;

// One evaluation per fragment, both outputs. ashFogF is how much air is in
// front of the surface and ashAirC is what colour that air is; every world
// shader reads exactly these two, which is the whole point of the file.
const GLSL_FULL = `
uniform vec4 ashAtmP;
uniform vec4 ashAtmA;
uniform vec4 ashAtmB;
uniform vec4 ashAtmQ;
float ashFogF;
vec3 ashAirC;
float ashPathM(float d, float y0, float y1) {
  float a = clamp((y0 - ashAtmP.w) * ashAtmP.z, -ashAtmQ.x, 12.0);
  float b = clamp((y1 - ashAtmP.w) * ashAtmP.z, -ashAtmQ.x, 12.0);
  float dd = b - a;
  float w = abs(dd) < 1e-3 ? exp(-a) : (exp(-a) - exp(-b)) / dd;
  return d * w;
}
void ashAtmo(vec3 wp) {
  float d = distance(camPos, wp);
  if (ashAtmP.x < 0.0) {
    ashFogF = clamp((d - fogSE.x) / max(fogSE.y - fogSE.x, 1.0), 0.0, 1.0);
    ashAirC = fogColor;
    return;
  }
  float r = ashAtmP.x * d;
  float tr = r * r;
  float tm = ashAtmP.y * ashPathM(d, camPos.y, wp.y);
  ashFogF = 1.0 - exp(-(tr + tm));
  vec3 vd = normalize(wp - camPos);
  float c = dot(vd, normalize(-sunDir));
  float w = 0.5 + 0.5 * c;
  vec3 air = fogColor * mix(vec3(1.0), ashAtmQ.yzw, tr / max(tr + tm, 1e-4));
  ashAirC = air * mix(ashAtmB.rgb, ashAtmA.rgb, w * w)
    * (1.0 + ashAtmA.w * pow(max(c, 0.0), ashAtmB.w));
}
`;

// THE VEGETATION LAYER'S PRIVATE ATMOSPHERE, AND WHY IT IS SCALED HERE.
// vegetation.js runs a SECOND extinction of its own on top of the mood fog:
//     float aer = (1.0 - exp(-dist * aerK)) * aerAmt;
//     fog = 1.0 - (1.0 - fog) * (1.0 - aer);
// with aerK 0.0016 and aerAmt 0.90 on the clear mood. That is 34% of a 300 m
// treeline replaced by air where the GROUND under the same trees, on the same
// mood, was at 0% — because the mood's linear band did not open until 700 m.
// The comment there says as much: "chosen against each mood's own fog ramp so
// the two are one continuous curve ... because its fog does not start until
// 700 m". It is a local workaround for the missing mechanism, authored per
// LAYER, and it is precisely the "the sky, the ground and the trees disagree at
// the horizon" failure this round exists to end.
// The mechanism now exists for every surface, so the private layer is scaled by
// ashAtmR.x (0 = the unified model alone). It is a uniform rather than a
// deletion for three reasons: vegetation.js is another dimension's file and its
// authored constants stay legible and revivable; the legacy A/B has to restore
// the layer or it would not be a control; and the next round gets a dial rather
// than an argument. aerCol — the misty inscatter TINT, which is a measured
// round-007 win — is untouched: only the extra extinction is.
// The same argument, for the NEAR end of the same layer. vegetation.js also
// bends the mood ramp with `fog = pow(fog, fogPow)` (1.55 on misty), because a
// LINEAR ramp opening at 8 m put a slice of veil on a 15 m shrub and the mood
// read as a flat tint instead of as depth. An exponential-squared column is
// quadratic near the camera by construction — that is the same fix, derived
// rather than dialled, and applied to every surface instead of to the leaves
// only. Scaled by ashAtmR.y (0 = exponent 1.0, i.e. the unified curve alone).
const VEG_POW_RE = /fog\s*=\s*pow\(fog,\s*fogPow\)\s*;/;
const VEG_AER_RE = /float\s+aer\s*=\s*\(1\.0\s*-\s*exp\(-dist\s*\*\s*aerK\)\)\s*\*\s*aerAmt\s*;/;
const GLSL_VEG = `
uniform vec4 ashAtmR;
`;

// the shared idiom in all ten shaders: clamp((<dist> - fogSE.x) / (fogSE.y -
// fogSE.x), 0.0, 1.0), with water's max(..., 1.0) guard as the only variation.
const FOG_RE = /clamp\(\s*\(\s*(.+?)\s*-\s*fogSE\.x\s*\)\s*\/\s*(?:max\(\s*)?\(?\s*fogSE\.y\s*-\s*fogSE\.x\s*\)?(?:\s*,\s*[0-9.]+\s*\))?\s*,\s*0\.0\s*,\s*1\.0\s*\)/g;
const MAIN_RE = /void main\s*\(\s*(?:void)?\s*\)\s*\{/;

const PATCHED = new Map();
const MATS = new Set();
let installed = false;

function patchFragment(key, kind) {
  const store = Effect.ShadersStore;
  const k = `${key}FragmentShader`;
  let src = store[k];
  if (!src || src.indexOf('ashAtmP') >= 0) return;
  if (!MAIN_RE.test(src) || src.indexOf('fogSE') < 0) return;
  const full = kind !== 'lite';
  if (full && (src.indexOf('camPos') < 0 || src.indexOf('sunDir') < 0)) return;
  if (!FOG_RE.test(src)) return;
  FOG_RE.lastIndex = 0;
  if (kind === 'veg' && !(VEG_AER_RE.test(src) && VEG_POW_RE.test(src))) return;
  // sunshadow.js wrapped the fog factor in its own receiver-height term. This
  // model integrates height along the ray instead, so unwrap it rather than
  // stack two height falloffs (see the header).
  src = src.replace(/ashFog\(\s*([A-Za-z_]\w*)\s*,\s*vPos\s*\)/g, '$1');
  // every one of these shaders measures its fog distance as the camera-to-
  // fragment distance (dist / camd / dCam / distance(camPos, vPos) — same
  // quantity, four spellings), so the captured expression is dropped and the one
  // shared evaluation is used instead.
  src = src.replace(FOG_RE, full ? 'ashFogF' : 'ashFogAmtD($1)');
  if (full) src = src.replace(MAIN_RE, (m) => `${m}\n  ashAtmo(vPos);\n  vec3 fogColor = ashAirC;`);
  if (kind === 'veg') {
    src = src.replace(VEG_AER_RE,
      'float aer = (1.0 - exp(-dist * aerK)) * aerAmt * ashAtmR.x;');
    src = src.replace(VEG_POW_RE, 'fog = pow(fog, mix(1.0, fogPow, ashAtmR.y));');
  }
  src = src.replace('void main',
    `${full ? GLSL_FULL : GLSL_LITE}${kind === 'veg' ? GLSL_VEG : ''}\nvoid main`);
  store[k] = src;
  PATCHED.set(key, kind);
}

// Uniform names must be in _options BEFORE the effect compiles — Babylon caches
// effects by name+defines, so pushing them later is a silent no-op. Same hook
// point as sunshadow.js (it chains: whichever installs first is wrapped by the
// second, and both run).
function hookShaderMaterial() {
  if (ShaderMaterial.prototype.__ashAtmHooked) return;
  const orig = ShaderMaterial.prototype.isReady;
  ShaderMaterial.prototype.isReady = function (...args) {
    if (this.__ashAtm === undefined) {
      const sp = this.shaderPath;
      const frag = typeof sp === 'string' ? sp : sp && sp.fragment;
      this.__ashAtm = typeof frag === 'string' ? (PATCHED.get(frag) || null) : null;
      if (this.__ashAtm) {
        const o = this.options;
        const want = this.__ashAtm === 'lite'
          ? ['ashAtmP', 'ashAtmQ']
          : ['ashAtmP', 'ashAtmA', 'ashAtmB', 'ashAtmQ'];
        if (this.__ashAtm === 'veg') want.push('ashAtmR');
        for (const u of want) if (o.uniforms.indexOf(u) < 0) o.uniforms.push(u);
        MATS.add(this);
      }
    }
    return orig.apply(this, args);
  };
  ShaderMaterial.prototype.__ashAtmHooked = true;
}

export class Atmosphere {
  constructor(scene) {
    this.scene = scene;
    // betaR < 0 is the legacy sentinel: until SkySystem pushes a mood the world
    // renders exactly the old linear model.
    this.p = new Vector4(-1, 0, 0.008, 0);
    this.a = new Vector4(1, 1, 1, 0);
    this.b = new Vector4(1, 1, 1, 6);
    this.q = new Vector4(1.25, 1, 1, 1);
    // x scales vegetation.js's private extinction (VEG_AER_RE), y its private
    // near-end ramp bend (VEG_POW_RE). Legacy restores both to 1 so the A/B
    // control is a true control.
    this.r = new Vector4(1, 1, 0, 0);
    this.baseMax = 1e9;
    this.on = true;
    this.cfg = null;
    scene.onAfterRenderTargetsRenderObservable.add(() => this.bind());
  }

  // Per-mood atmosphere, pushed from SkySystem.applyEnv (which owns the
  // authoring — see the ATM table in src/world/sky.js).
  //   betaR/betaM  1/m, the two optical-depth coefficients
  //   hK           1/m, height falloff of the ground layer (the mood's fogH)
  //   warm/cool    rgb multipliers on fogColor into / away from the sun
  //   mie          forward-lobe gain, miePow its exponent
  //   ray          rgb multiplier on the RAYLEIGH half of the inscatter (blue)
  //   cap          max air density below the datum
  //   vegAer       how much of vegetation.js's private extinction survives
  //   vegPow       ...and how much of its private near-end ramp bend
  //   baseMax      ceiling the datum follows the camera up to (valley mist)
  setEnv(c) {
    this.cfg = c;
    this.p.x = this.on ? c.betaR : -1;
    this.p.y = c.betaM;
    this.p.z = c.hK;
    this.a.set(c.warm[0], c.warm[1], c.warm[2], c.mie);
    this.b.set(c.cool[0], c.cool[1], c.cool[2], c.miePow);
    this.q.set(Math.log(Math.max(1, c.cap)), c.ray[0], c.ray[1], c.ray[2]);
    this.r.x = this.on ? (c.vegAer ?? 0) : 1;
    this.r.y = this.on ? (c.vegPow ?? 0) : 1;
    this.baseMax = c.baseMax ?? 1e9;
    this.setBase(this.camY ?? 0);
  }

  // The ground layer's datum is a WORLD height that follows the camera only up
  // to the mood's ceiling, so valley mist stays in the valley when you climb out
  // of it instead of riding up the ridge with you (same contract as
  // sunshadow.setFogBase, which this replaces).
  setBase(y) {
    this.camY = y;
    this.p.w = Math.min(y, this.baseMax);
  }

  // runtime A/B + escape hatch: window.__atmo.legacy(true) is the exact
  // pre-round-009 linear model, in every shader, with no rebuild.
  legacy(v) {
    this.on = !v;
    if (this.cfg) this.setEnv(this.cfg);
    return !this.on;
  }

  bind() {
    if (!MATS.size) return;
    for (const m of MATS) {
      m.setVector4('ashAtmP', this.p);
      m.setVector4('ashAtmQ', this.q);
      if (m.__ashAtm === 'lite') continue;
      m.setVector4('ashAtmA', this.a);
      m.setVector4('ashAtmB', this.b);
      if (m.__ashAtm === 'veg') m.setVector4('ashAtmR', this.r);
    }
  }
}

// Must run before any world material is constructed (createWorld() is called
// first thing in main()), and AFTER installWorldShadows() so the ashFog unwrap
// above has something to unwrap.
export function installAtmosphere(scene) {
  if (installed) return scene.__ashAtmo;
  installed = true;
  for (const k of Object.keys(FRAGS)) patchFragment(k, FRAGS[k]);
  hookShaderMaterial();
  const sys = new Atmosphere(scene);
  // ?atm=0 boots the whole world on the pre-round-009 linear model. It is the
  // A/B control the beauty-shot harness needs — the only way to shoot a legacy
  // set and a candidate set through the SAME build, the same streaming settle
  // and the same concurrent work by other dimensions, so a drift can be
  // attributed to this mechanism rather than to the day's other commits.
  try {
    if (new URLSearchParams(location.search).get('atm') === '0') sys.legacy(true);
  } catch (e) { /* no location (worker/test host): keep the atmosphere on */ }
  sys.mats = MATS;
  sys.patched = PATCHED;
  scene.__ashAtmo = sys;
  window.__atmo = sys;
  return sys;
}
